import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import React, { CSSProperties } from 'react';
import Image from '../../primitives/image';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import { useLocator } from '../../../contexts/franchise/locator-provider';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import PhoneNumber from '../../primitives/phone-number';
import { colors } from '../../primitives/colors';
import Col from '../../primitives/grid/col';
import Container from '../../primitives/grid/container';
import Row from '../../primitives/grid/row';
import RowSet from '../../primitives/grid/row-set';
import Skeleton from '../../primitives/skeleton';
import { StyledButtonAsLink } from '../../primitives/styled-button';
import { breakpoints } from '../../primitives/tokens';
import Typography from '../../primitives/typography';
import formatPhoneLink from '../../utils/format-phone-link';
import VideoPlayer from './video-player';
import AspectRatio from '../../primitives/aspect-ratio';

// TODO: Ensure these interfaces don't exist anywhere else

interface Content {
    headline?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    headlineType?: any;
    copy?: string;
    link?: ContentStackLink
    enableLocalCta?: boolean;
}

interface Video {
    filename: string;
    title: string;
    url: string;
}

interface Media {
    image?: ContentStackImage;
    video?: Video;
    externalVideoEmbed?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    externalVideoEmbedThumbnail?: any;
    externalVideoEmbedMobile?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    externalVideoEmbedMobileThumbnail?: any;
    isAutoplay?: boolean;
}

interface Settings {
    hasBreadcrumbs?: boolean;
    isFlipped?: boolean;
    mediaFill?: boolean;
    hideBackground?: boolean;
    mediaFullWidth?: boolean;
    darkMode?: boolean;
}

interface MediaContentData {
    content?: Content;
    media?: Media;
    settings?: Settings;
}

interface TextOverlayProps {
    children: React.ReactNode;
}

interface HeadlineProps {
    as?: React.ElementType;
    children?: string;
}

interface VideoObject {
    type: 'embed' | 'hosted';
    src: string;
}

const styles = {
    textOverlay: css({
        position: 'absolute',
        color: colors.shadesWhite,
        zIndex: 1,
        transform: 'translate(-50%, -50%)',
        top: '50%',
        left: '50%',
        textAlign: 'center',
        width: '100%',
        animation: 'fadeIn 1.5s',
        '& @keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 },
        },
        '& *': {
            textShadow: '1px 2px 3px rgba(0, 0, 0, 0.4)',
        },
        '& h1': {
            fontSize: '54px',
            '@media (max-width: 768px)': {
                fontSize: '48px',
            },
        },
        '& h2': {
            fontSize: '48px',
            '@media (max-width: 768px)': {
                fontSize: '36px',
            },
        },
        '& h3': {
            fontSize: '36px',
            '@media (max-width: 768px)': {
                fontSize: '30px',
            },
        },
        '& h4': {
            fontSize: '30px',
            '@media (max-width: 768px)': {
                fontSize: '24px',
            },
        },
        '& h5': {
            fontSize: '24px',
            '@media (max-width: 768px)': {
                fontSize: '18px',
            },
        },
    }),

    // Base media wrapper
    mediaFullWidthWrapper: css({
        background: colors.grey100,
        position: 'relative',
    }),

    // Modifier for hiding background
    mediaFullWidthWrapperHideBackground: css({
        background: colors.shadesWhite,
        position: 'relative',
    }),

    mediaFullWidthInner: css({
        position: 'relative',
        display: 'block',
        '&:before': {
            content: '""',
            display: 'block',
            paddingTop: '56.25%',
        },
    }),

    mediaFullWidthLink: css({
        position: 'absolute',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        zIndex: 2,
        bottom: '6vw',
        [breakpoints.tb]: {
            bottom: '64px',
        },
        [breakpoints.mb]: {
            bottom: '32px',
        },
    }),

    videoPlayerDesktop: css({
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        [breakpoints.mb]: {
            display: 'none',
        },
    }),

    videoPlayerMobile: css({
        display: 'none',
        [breakpoints.mb]: {
            display: 'block',
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
        },
    }),

    // Base default wrapper
    defaultWrapper: css({
        paddingTop: '48px',
        paddingBottom: '48px',
        position: 'relative',
        [breakpoints.mb]: {
            padding: '0',
            minHeight: 'unset',
        },
    }),

    // Modifier for full size
    defaultWrapperFullSize: css({
        paddingTop: '0',
        paddingBottom: '0',
        position: 'relative',
        [breakpoints.mb]: {
            padding: '0',
            minHeight: 'unset',
        },
    }),

    // Modifier for hiding background
    defaultWrapperHideBackground: css({
        paddingTop: '48px',
        paddingBottom: '48px',
        position: 'relative',
        [breakpoints.mb]: {
            padding: '0',
            minHeight: 'unset',
        },
    }),

    // Modifier for both full size and hide background
    defaultWrapperFullSizeHideBackground: css({
        paddingTop: '0',
        paddingBottom: '0',
        position: 'relative',
        [breakpoints.mb]: {
            padding: '0',
            minHeight: 'unset',
        },
    }),

    mobileImageWrapper: css({
        position: 'relative',
        width: '100%',
        display: 'none',
        [breakpoints.mb]: {
            display: 'block',
        },
    }),

    mobileGatsbyImage: css({
        width: '100%',
        height: '100%',
    }),

    videoPlayer: css({
        objectFit: 'cover',
    }),

    // Base container
    container: css({
        display: 'flex',
        [breakpoints.mb]: {
            minHeight: '0px',
        },
    }),

    // Modifier for full size container
    containerFullSize: css({
        minHeight: '620px',
        display: 'flex',
        [breakpoints.mb]: {
            minHeight: '0px',
        },
    }),

    colLeft: css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }),

    // Base image container
    imageContainer: css({
        display: 'flex',
        position: 'relative',
        flexDirection: 'column',
        flexShrink: 0,
        width: '100%',
        top: '0',
        left: '0',
        bottom: '0',
        clipPath: 'none',
        video: {
            maxHeight: '100%',
        },
        [breakpoints.mb]: {
            display: 'none',
        },
    }),

    // Modifier for full size image container
    imageContainerFullSize: css({
        display: 'flex',
        position: 'absolute',
        flexDirection: 'column',
        flexShrink: 0,
        width: '56.75%',
        top: '0',
        left: '0',
        bottom: '0',
        clipPath: 'polygon(0% 0%, 88% 0%, 100% 50%, 88% 100%, 0% 100%)',
        video: {
            maxHeight: '100%',
        },
        [breakpoints.uw]: {
            width: '50vw',
        },
        [breakpoints.tb]: {
            width: '45vw',
        },
        [breakpoints.mb]: {
            display: 'none',
        },
    }),

    // Modifier for flipped image container
    imageContainerFlipped: css({
        display: 'flex',
        position: 'relative',
        flexDirection: 'column',
        flexShrink: 0,
        width: '100%',
        top: '0',
        left: 'unset',
        right: '0',
        bottom: '0',
        clipPath: 'none',
        video: {
            maxHeight: '100%',
        },
        [breakpoints.mb]: {
            display: 'none',
        },
    }),

    // Modifier for full size + flipped image container
    imageContainerFullSizeFlipped: css({
        display: 'flex',
        position: 'absolute',
        flexDirection: 'column',
        flexShrink: 0,
        width: '56.75%',
        top: '0',
        left: 'unset',
        right: '0',
        bottom: '0',
        clipPath: 'polygon(12% 0%, 100% 0%, 100% 100%, 12% 100%, 0% 50%)',
        video: {
            maxHeight: '100%',
        },
        [breakpoints.uw]: {
            width: '50vw',
        },
        [breakpoints.tb]: {
            width: '45vw',
        },
        [breakpoints.mb]: {
            display: 'none',
        },
    }),

    gatsbyImage: css({
        width: '100%',
        height: '100%',
    }),

    videoPlayerDefault: {
        objectFit: 'cover',
        margin: 'auto',
        minWidth: '100%',
    } as CSSProperties,

    colRight: css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }),

    // Base row set wrapper
    rowSetWrapper: css({
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '100%',
    }),

    // Modifier for flipped row set wrapper
    rowSetWrapperFlipped: css({
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        width: '100%',
    }),

    // Base row set
    rowSet: css({
        padding: '0',
        width: '100%',
        [breakpoints.mb]: {
            paddingTop: '24px',
            paddingBottom: '32px',
            width: '100%',
        },
        margin: '0',
    }),

    // Modifier for full size row set
    rowSetFullSize: css({
        padding: '20% 0',
        width: '86%',
        [breakpoints.mb]: {
            paddingTop: '24px',
            paddingBottom: '32px',
            width: '100%',
        },
        margin: '0',
    }),

    richTextStyles: css(RichTextEditorStyles, {
        p: {
            fontSize: '18px',
            lineHeight: '28px',
            '@media (min-width: 1280px)': {
                fontSize: '20px',
                lineHeight: '28px',
            },
        },
        li: {
            fontSize: '18px',
            lineHeight: '28px',
            '@media (min-width: 1280px)': {
                fontSize: '20px',
                lineHeight: '28px',
            },
        },
        button: {
            display: 'inline-block',
            overflow: 'hidden',
            position: 'relative',
            paddingRight: '40px',
            marginBottom: '32px',
            marginTop: '48px',
            borderColor: '#ffffff',
            whiteSpace: 'nowrap',
            '&::before': {
                backgroundColor: colors.primaryOrange,
                content: "''",
                width: '46px',
                display: 'block',
                position: 'absolute',
                top: '0',
                bottom: '0',
                right: '24px',
                transform: 'rotate(45deg)',
            },
            '&::after': {
                width: '46px',
                content: "''",
                borderColor: colors.primaryOrange,
                transform: 'rotate(45deg)',
                display: 'block',
                position: 'absolute',
                top: '0',
                bottom: '0',
                right: '12px',
                borderTopWidth: '4px',
                borderRightWidth: '4px',
            },
            span: {
                backgroundColor: colors.primaryOrange,
                display: 'inline-block',
                position: 'relative',
                zIndex: 30,
                paddingTop: '12px',
                paddingBottom: '12px',
                paddingRight: '28px',
                paddingLeft: '40px',
                borderRadius: '9999px',
                borderTopRightRadius: '0',
                borderBottomRightRadius: '0',
                fontSize: '18px',
                lineHeight: 1.25,
                fontWeight: 600,
                color: colors.shadesWhite,
            },
        },
        '@media (min-width: 1280px)': {
            fontSize: '18px',
            lineHeight: '28px',
        },
    }),

    linkButtonWrapper: css({
        display: 'flex',
        alignSelf: 'start',
    }),

    localCtaWrapper: css({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        alignSelf: 'flex-start',
        width: 'auto',
        [breakpoints.tb]: {
            justifyContent: 'center',
            width: '100%',
        },
    }),
};

const TextOverlay: React.FC<TextOverlayProps> = ({ children }) => {
    if (!children) {
        return null;
    }

    return <div css={styles.textOverlay}>{children}</div>;
};

const Headline: React.FC<HeadlineProps> = (props) => {
    const { as: Component, children } = props;
    if (!children) {
        return null;
    }
    const sanitizedHtml = apCaseOnlyTitleTags(children);
    return Component ? (
        <Component
            dangerouslySetInnerHTML={{
                __html: sanitizedHtml,
            }}
        />
    ) : (
        <h3
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
                __html: sanitizedHtml,
            }}
        />
    );
};

function generateVideoObject(videoUrl?: string, embedVideoUrl?: string): VideoObject | null {
    if (!(videoUrl || embedVideoUrl)) {
        return null;
    }
    return {
        type: embedVideoUrl ? 'embed' : 'hosted',
        src: embedVideoUrl || videoUrl || '',
    };
}
const MediaContent: React.FC<MediaContentData> = (data) => {
    const [showHeadlineOverlay, setShowHeadlineOverlay] = React.useState(false);

    const {
        franchise: micrositeFranchise,
    } = useMicrosite();
    const { franchise: locatorFranchise } = useLocator();
    const franchise = micrositeFranchise || locatorFranchise?.data;

    const { mainPhone: frPhone } = franchise ?? {};
    const { content, media, settings } = data || {};
    const { headline, headlineType, copy, link, enableLocalCta } = content || {};
    const {
        image,
        video,
        externalVideoEmbed,
        externalVideoEmbedThumbnail,
        externalVideoEmbedMobile,
        externalVideoEmbedMobileThumbnail,
        isAutoplay,
    } = media ?? {};

    const {
        isFlipped,
        mediaFill: fullSize,
        hideBackground,
        mediaFullWidth,
        darkMode,
    } = settings || {};

    const videoObject = generateVideoObject(video?.url, externalVideoEmbed);

    const videoObjectMobile = generateVideoObject(
        video?.url,
        externalVideoEmbedMobile,
    );

    const theme = {
        light: {
            background: hideBackground ? colors.shadesWhite : colors.grey100,
            text: colors.shadesBlack,
            headline: colors.shadesBlack,
        },
        dark: {
            background: colors.shadesBlack,
            text: colors.shadesWhite,
            headline: colors.primaryOrange,
        },
    };

    if (mediaFullWidth && videoObject?.src) {
        return (
            <section css={[{
                ...theme[darkMode ? 'dark' : 'light'],
                position: 'relative',
            }]}
            >
                {showHeadlineOverlay && (
                    <TextOverlay>
                        {headline && (
                            <Headline as={headlineType || 'h2'}>
                                {headline}
                            </Headline>
                        )}
                    </TextOverlay>
                )}
                <div css={styles.mediaFullWidthInner}>
                    {link?.title && (
                        <div css={styles.mediaFullWidthLink}>
                            <StyledButtonAsLink to={link.href || '/'}>
                                {link?.title}
                            </StyledButtonAsLink>
                        </div>
                    )}
                    <VideoPlayer
                        css={styles.videoPlayerDesktop}
                        thumbnail={externalVideoEmbedThumbnail}
                        url={videoObject?.src}
                        onPlay={() => {
                            if (headline) {
                                setShowHeadlineOverlay(true);
                            }
                        }}
                        playing
                        loop
                        controls={false}
                        volume={0}
                        muted
                        playsinline
                        width="100%"
                        height="100%"
                    />
                    <VideoPlayer
                        css={styles.videoPlayerMobile}
                        thumbnail={externalVideoEmbedMobileThumbnail}
                        url={videoObjectMobile?.src}
                        onPlay={() => {
                            if (headline) {
                                setShowHeadlineOverlay(true);
                            }
                        }}
                        playing
                        loop
                        controls={false}
                        volume={0}
                        muted
                        playsinline
                        width="100%"
                        height="100%"
                    />
                </div>
            </section>
        );
    }

    return (
        <section css={[{
            padding: fullSize ? '0' : '48px 0',
            ...theme[darkMode ? 'dark' : 'light'],
            position: 'relative',
            [breakpoints.mb]: {
                padding: '0',
                minHeight: 'unset',
            },
        }]}
        >
            <div css={styles.mobileImageWrapper}>
                {!!image?.url && (
                    <Image
                        css={styles.mobileGatsbyImage}
                        image={image}
                        alt={image.description ?? undefined}
                    />
                )}
                {!!videoObject?.src && (
                    <VideoPlayer
                        url={videoObject.src}
                        playing={!!isAutoplay}
                        loop={!!isAutoplay}
                        controls={
                            !(videoObject.type === 'hosted' && !!isAutoplay)
                        }
                        playsinline
                        volume={isAutoplay ? 0 : 1}
                        muted={!!isAutoplay}
                        width="100%"
                        height="100%"
                        css={styles.videoPlayer}
                    />
                )}
            </div>
            <Container
                css={[
                    styles.container,
                    fullSize && styles.containerFullSize,
                ]}
            >
                <Row css={{ position: 'unset' }}>
                    <Col
                        breakpoints={fullSize ? {
                            dt: { span: 7, start: isFlipped ? 5 : 0 },
                            tb: { span: 6, start: isFlipped ? 6 : 0 },
                            mb: { span: 4 },
                        } : {
                            dt: { span: 5, start: isFlipped ? 7 : 0 },
                            tb: {
                                span: 6,
                                start: isFlipped ? 6 : 0,
                                rightGap: isFlipped ? 'none' : 'full',
                                leftGap: isFlipped ? 'full' : 'none',
                            },
                            mb: { span: 4, start: 0, hidden: true },
                        }}
                        css={styles.colLeft}
                    >
                        <div css={[
                            styles.imageContainer,
                            fullSize && styles.imageContainerFullSize,
                            isFlipped && styles.imageContainerFlipped,
                            fullSize && isFlipped && styles.imageContainerFullSizeFlipped,
                        ]}
                        >
                            {!!image?.url && (
                                <Image
                                    css={styles.gatsbyImage}
                                    image={image}
                                    alt={image.description ?? undefined}
                                />
                            )}
                            {!!videoObject?.src && !fullSize && (
                                <AspectRatio breakpoints={{ dt: { width: 16, height: 9 } }}>
                                    <VideoPlayer
                                        css={{
                                            height: '100%',
                                        }}
                                        url={videoObject.src}
                                        playing={!!isAutoplay}
                                        loop={!!isAutoplay}
                                        controls={
                                            !(videoObject.type === 'hosted' && !!isAutoplay)
                                        }
                                        playsinline
                                        volume={isAutoplay ? 0 : 1}
                                        muted={!!isAutoplay}
                                        width="100%"
                                        height="100%"
                                        background={hideBackground ? colors.shadesWhite : colors.grey100}
                                        style={styles.videoPlayerDefault}
                                    />
                                </AspectRatio>
                            )}
                            {!!videoObject?.src && fullSize && (
                                <VideoPlayer
                                    url={videoObject.src}
                                    playing={!!isAutoplay}
                                    loop={!!isAutoplay}
                                    controls={
                                        !(videoObject.type === 'hosted' && !!isAutoplay)
                                    }
                                    playsinline
                                    volume={isAutoplay ? 0 : 1}
                                    muted={!!isAutoplay}
                                    width="100%"
                                    height="auto"
                                    background={hideBackground ? colors.shadesWhite : colors.grey100}
                                    style={styles.videoPlayerDefault}
                                />
                            )}
                        </div>
                    </Col>
                    <Col
                        breakpoints={fullSize ? {
                            dt: { span: 5, start: isFlipped ? 0 : 7 },
                            tb: { span: 6, start: isFlipped ? 0 : 6 },
                            mb: { span: 4, start: 0 },
                        } : {
                            dt: { span: 6, start: isFlipped ? 0 : 6 },
                            mb: { span: 4, start: 0 },
                        }}
                        css={styles.colRight}
                    >
                        <div css={[
                            styles.rowSetWrapper,
                            isFlipped && styles.rowSetWrapperFlipped,
                        ]}
                        >
                            <RowSet
                                css={[
                                    styles.rowSet,
                                    fullSize && styles.rowSetFullSize,
                                ]}
                                breakpoints={{
                                    dt: { between: 32 },
                                    mb: { between: 16 },
                                }}
                            >
                                {!!headline && (
                                    <Typography
                                        as={headlineType || 'h3'}
                                        reducedLetterSpacing
                                        fontWeight="regular"
                                        dangerouslySetInnerHTML={{ __html: headline }}
                                        fontSize={{
                                            dt: '3xl',
                                            tb: '2xl',
                                            mb: '2xl',
                                        }}
                                        css={{
                                            color: theme[darkMode ? 'dark' : 'light'].headline,
                                        }}
                                    />
                                )}
                                <div
                                    css={[styles.richTextStyles, {
                                        color: theme[darkMode ? 'dark' : 'light'].text,
                                    }]}
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{ __html: copy || '' }}
                                />
                                {(link?.title && link?.href) && (
                                    <div css={styles.linkButtonWrapper}>
                                        <StyledButtonAsLink to={link.href}>{link.title}</StyledButtonAsLink>
                                    </div>
                                )}
                                {!!enableLocalCta && (
                                    <div css={styles.localCtaWrapper}>
                                        <Skeleton>
                                            {frPhone && (
                                                <RowSet breakpoints={{
                                                    dt: { between: 16 },
                                                    mb: { between: 8 },
                                                }}
                                                >
                                                    <PhoneNumber as={StyledButtonAsLink} to={`tel:${formatPhoneLink(frPhone)}`}>
                                                        {formatPhoneLink(frPhone)}
                                                    </PhoneNumber>
                                                    <Typography
                                                        as="h5"
                                                        fontWeight="medium"
                                                        css={{
                                                            color: theme[darkMode ? 'dark' : 'light'].text,
                                                        }}
                                                    >
                                                        Contact Your Local SERVPRO
                                                    </Typography>
                                                </RowSet>
                                            )}
                                        </Skeleton>
                                    </div>
                                )}
                            </RowSet>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
};

export const query = graphql`
    fragment MediaContentData on CsMediaContent {
        ... on CsMediaContent {
            content {
                headline
                headlineType
                copy
                enableLocalCta
                link {
                    href
                    title
                }
            }
            media {
                externalVideoEmbed
                externalVideoEmbedThumbnail {
                    childImageSharp {
                        gatsbyImageData
                    }
                    publicURL
                }
                externalVideoEmbedMobile
                externalVideoEmbedMobileThumbnail {
                   childImageSharp {
                       gatsbyImageData
                   }
                   publicURL
                }
                video {
                    filename
                    title
                    url
                }
                isAutoplay
                image {
                    filename
                    title
                    description
                    url
                    contentStackAsset
                }
            }
            settings {
                hasBreadcrumbs
                isFlipped
                mediaFill
                mediaFullWidth
                hideBackground
                darkMode
            }
        }
    }
`;

export default MediaContent;
